<template>
    <!-- *********************************************
    *                     Header                     *
    ********************************************** -->
    <div v-if="!getMobileAppState" class="header-area">
        <div class="align-helper">
            <!-- <button-tertiary
            buttonStyle="round"
            buttonSize="small"
            icon="ticket"
            :label="t('login.ticketBtn')"
            @clicked="navigateByName(routeList.NON_AUTHENTICATED_TICKETS_NAME)"/> -->
        </div>

        <header-logo class="header-logo"/>

        <div class="align-helper right-side">
            <dropdown-more-options
            icon="more"
            variant="tertiary"
            :options="moreOptions"
            @clicked="moreOptionsClickHandler"/>
        </div>
    </div>
    <div
    v-else
    class="header-area-mobile"
    :class="{ 'mobile-status-bar-android': getDeviceInUse === 'android', 'mobile-status-bar-ios': getDeviceInUse === 'ios' }">
        <div class="buttons-wrapper">
            <!-- <button-tertiary
            buttonStyle="round"
            buttonSize="small"
            icon="ticket"
            @clicked="navigateByPath(routeList.NON_AUTHENTICATED_TICKETS_PATH)"/> -->
            <dropdown-more-options
            icon="more"
            variant="tertiary"
            :options="moreOptions"
            @clicked="moreOptionsClickHandler"/>
        </div>
    </div>
    <!-- *********************************************
    *                      Form                      *
    ********************************************** -->
    <div class="form-wrapper" :class="{ 'mobile': getMobileAppState }">
        <div class="personal-area">
            <img class="user-profile-pic" :src="avatarBackgroundUrl" @error="errorImage"/>
            <div class="contract-name">{{visibleName}}</div>
            <div class="password-helper">{{t('login.insertRememberMePassword')}}</div>
            <!-- Change Acession Button -->
            <button-tertiary
            v-if="!getMobileAppState && getClientType === 'company'"
            buttonSize="small"
            icon="saved-login"
            :label="t('login.changeAcessionBtn')"
            @clicked="emit('showAcessionModal')"/>
        </div>
        <!-- Password Area -->
        <div class="password-area">
            <div class="password-inputs">
                <span
                v-for="(el, index) in 6"
                :key="el"
                class="char-circle"
                :class="{'active': password.length === index, 'complete': password.length > index}"/>
            </div>

            <div class="pad-area" ref="padArea">
                <button-remember-me-pad
                label="1"
                name="button1"
                @clicked="emit('updatePassword', password + '1')"/>
                <button-remember-me-pad
                label="2"
                name="button2"
                @clicked="emit('updatePassword', password + '2')"/>
                <button-remember-me-pad
                label="3"
                name="button3"
                @clicked="emit('updatePassword', password + '3')"/>
                <button-remember-me-pad
                label="4"
                name="button4"
                @clicked="emit('updatePassword', password + '4')"/>
                <button-remember-me-pad
                label="5"
                name="button5"
                @clicked="emit('updatePassword', password + '5')"/>
                <button-remember-me-pad
                label="6"
                name="button6"
                @clicked="emit('updatePassword', password + '6')"/>
                <button-remember-me-pad
                label="7"
                name="button7"
                @clicked="emit('updatePassword', password + '7')"/>
                <button-remember-me-pad
                label="8"
                name="button8"
                @clicked="emit('updatePassword', password + '8')"/>
                <button-remember-me-pad
                label="9"
                name="button9"
                @clicked="emit('updatePassword', password + '9')"/>
                <button-remember-me-pad
                v-if="getMobileAppState"
                :icon="getMobileBiometric === 'face' ? 'face-id' : 'fingerprint'"
                :style="{
                    'opacity': selectedAcession.hasBiometricEnrolled ? '1': '0',
                    'pointer-events': selectedAcession.hasBiometricEnrolled ? 'all': 'none'
                }"
                @clicked="emit('requestBiometricChallenge')"/>
                <button-remember-me-pad
                class="last-number-button"
                label="0"
                name="button0"
                @clicked="emit('updatePassword', password + '0')"/>
                <button-remember-me-pad
                icon="backspace"
                name="backspace"
                @clicked="emit('updatePassword', password.slice(0, -1))"/>
            </div>
        </div>
    </div>
    <!-- *********************************************
    *              Remove Acession Modal             *
    ********************************************** -->
    <modal-centered-generic
    v-if="showRemoveAcessionModal"
    negative
    titleIcon="delete"
    rightButtonIcon="check-line"
    :title="t('login.removeAcessionModal.title')"
    :text="t('login.removeAcessionModal.text', {contractName: selectedAcession.contractName || maskedAccessionNumber})"
    :leftButtonText="t('login.removeAcessionModal.leftButtonText')"
    :rightButtonText="t('login.removeAcessionModal.rightButtonText')"
    @hideMe="showRemoveAcessionModal = false"
    @rightButtonClicked="removeAcessionHandler"/>

    <!-- Feedback toast -->
    <toast-generic
    v-if="showFeedbackToast"
    toastType="success"
    :feedbackText="t('login.successFeedbackToast')"
    @hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import ButtonTertiary from '@/components/generic/buttons/Tertiary'
import DropdownMoreOptions from '@/components/generic/dropdowns/moreOptions/MoreOptions'
import ButtonRememberMePad from '@/components/generic/buttons/RememberMePad'
import ModalCenteredGeneric from '@/components/generic/modals/centered/Generic'
import ToastGeneric from '@/components/generic/toasts/Generic'
/* vue */
import { ref, computed, defineAsyncComponent, onBeforeUnmount, onMounted } from 'vue'
import { firstLastWordBuilder } from '@/assets/scripts/strings'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['updatePassword', 'showAcessionModal', 'goToRegLogin', 'showFeedback', 'requestBiometricChallenge'])

/* mapActions */
const { updateAppThemeClientType, updateAppThemeMode } = mapActions()
const { removeAcessionFromEntityRememberMeInfo, updateSelectedEntityRememberInfo } = mapActions(moduleMapper.SESSION)
const { getClientType, getAppTheme, getAppThemeMode, getMobileAppState, getDeviceInUse } = mapGetters()
const { getRememberMeEntityInfo, getMobileBiometric } = mapGetters(moduleMapper.SESSION)

/** Props received:
@password { String, Number }
@selectedAcessionType { String }
*/

const props = defineProps({
    password: [String, Number],
    selectedAcessionType: String
})

const selectedAcession = computed(() => {
    return getRememberMeEntityInfo.value.listOfAcessions.find(el => el.selected) || getRememberMeEntityInfo.value.listOfAcessions[0]
})

const visibleName = computed(() => {
    const name = selectedAcession.value.customerName ? firstLastWordBuilder(selectedAcession.value.customerName) : selectedAcession.value.contractName || maskedAccessionNumber.value
    return name
})

const headerLogo = computed(() => {
    const theme = getAppTheme.value
	return defineAsyncComponent(() => import('@/assets/images/logo-bai-' + theme + '-color.svg?inline'))
})

const avatarBackgroundUrl = computed(() => {
    return selectedAcession.value.avatarImageUrl || Promise.reject
})

const maskedAccessionNumber = computed(() => {
    const accessionNumber = selectedAcession.value.acessionNumber
    const lastDigits = accessionNumber.slice(accessionNumber.length - 2, accessionNumber.length)

    return lastDigits.toString().padStart(accessionNumber.length, '*')
})

const errorImage = event => {
    event.target.src = getAppThemeMode.value === 'dark' ? './images/user_image_placeholder_dark.svg' : './images/user_image_placeholder.svg'
}

const moreOptionsClickHandler = $event => {
    moreOptions.find(obj => obj.id === $event).callbackFunction()
}

const removeAcessionHandler = () => {
    removeAcessionFromEntityRememberMeInfo(selectedAcession.value.acessionNumber)
    toggleRemoveAcessionModalHandler()
    showFeedbackToast.value = true

    if (getRememberMeEntityInfo.value && !getRememberMeEntityInfo.value.listOfAcessions.find(el => el.selected)) {
        updateSelectedEntityRememberInfo(getRememberMeEntityInfo.value.listOfAcessions[0].acessionNumber)
        updateAppThemeClientType(getRememberMeEntityInfo.value.listOfAcessions.find(el => el.selected).type.toLowerCase())
    }

    if (!getRememberMeEntityInfo.value) {
        emit('goToRegLogin')
        emit('showFeedback')
    }
}

const showFeedbackToast = ref(false)
const showRemoveAcessionModal = ref(false)
const toggleRemoveAcessionModalHandler = () => {
    showRemoveAcessionModal.value = !showRemoveAcessionModal.value
}
const moreOptions = [{
    id: 2,
    text: t('login.goToLoginBtn'),
    icon: 'logout',
    callbackFunction: () => emit('goToRegLogin')
}, {
    id: 3,
    text: t('login.deleteAcessionBtn'),
    icon: 'delete',
    variant: 'negative',
    callbackFunction: toggleRemoveAcessionModalHandler
}]

const padArea = ref('')
const keydownHandler = event => {
    if (props.password.length === 6) return
    switch (event.key) {
        case '1':
        case '2':
        case '3':
        case '4':
        case '5':
        case '6':
        case '7':
        case '8':
        case '9':
        case '0':
            padArea.value.children[`button${event.key}`].focus()
            emit('updatePassword', props.password + event.key)
            setTimeout(() => {
                padArea.value.children[`button${event.key}`].blur()
            }, 100)
            break
        case 'Backspace':
            padArea.value.children.backspace.children[0].focus()
            emit('updatePassword', props.password.slice(0, -1))
            setTimeout(() => {
                padArea.value.children.backspace.children[0].blur()
            }, 100)
            break
        default:
            break
    }
}

document.addEventListener('keydown', keydownHandler)
onBeforeUnmount(() => {
    document.removeEventListener('keydown', keydownHandler)
})

onMounted(() => {
    if (selectedAcession.value.selectedAppTheme) {
        updateAppThemeMode(selectedAcession.value.selectedAppTheme)
    }
    if (getMobileAppState.value && getClientType.value === 'company') {
        moreOptions.unshift({
            id: 1,
            text: t('login.changeAcessionBtn'),
            icon: 'saved-login',
            callbackFunction: () => emit('showAcessionModal')
        })
    }
})
</script>

<style lang="scss" scoped>
.header-area {
    width: 100%;
    display: flex;
    justify-content: space-between;

    padding: 24px 24px 0 24px;
    margin-bottom: 48px;

    .align-helper {
        width: 102px;

        &.right-side {
            display: flex;
            justify-content: flex-end;
        }
    }
}

.header-area-mobile {
    width: 100%;
    height: 88px;

    display: flex;
    flex-shrink: 0;
    flex-direction: row;
    justify-content: flex-end;
    position: relative;

    padding: 16px;
    background-image: var(--gradient-horizontal);

    &.mobile-status-bar-android {
        height: calc(88px + var(--android-top-padding)); /*Android status bar*/
        padding-top: calc(16px + var(--android-top-padding));
    }

    &.mobile-status-bar-ios {
        height: calc(88px + constant(safe-area-inset-top)); /* iOS 11.0 */
        height: calc(88px + env(safe-area-inset-top)); /* iOS 11.2 */

        padding-top: calc(16px + constant(safe-area-inset-top));
        padding-top: calc(16px + env(safe-area-inset-top));
    }

    &::after {
        content: '';
        position: absolute;
        left: 0;
        bottom: 0;

        width: 100%;
        height: 16px;

        border-radius: 16px 16px 0 0;
        background-color: var(--secondary-light-4);
    }

    svg {
        height: 32px;
    }

    .buttons-wrapper {
        display: flex;

        gap: 16px;
    }
}
.form-wrapper {
    width: 100%;
    max-width: 375px;
    height: 100%;
    min-height: 446px;

    display: flex;
    flex-direction: column;
    align-items: center;

    .personal-area {
        display: flex;
        flex-direction: column;
        align-items: center;
        position: relative;

        margin-bottom: 36px;

        .user-profile-pic {
            width: 120px;
            height: 120px;
            margin-bottom: 16px;

            border-radius: 100%;
            object-fit: cover;
        }

        .acronym-wrapper {
            width: 32px;
            height: 32px;

            display: flex;
            align-items: center;
            justify-content: center;
            position: absolute;
            top: 104px;

            background-color: var(--secondary-light-4);
            border: 1px solid var(--secondary-light-2);
            border-radius: 6px;

            @include H7-Medium;
            line-height: 16px;
            color: var(--secondary-dark-2);
        }

        .contract-name {
            @include H3-Medium;
            line-height: 36px;
            color: var(--primary);
            text-transform: capitalize;
        }

        .password-helper {
            @include H7-Medium;
            line-height: 16px;
            color: var(--secondary-dark-1);

            margin-bottom: 16px;
        }
    }

    .password-area {
        width: 100%;
        height: 100%;
        min-height: 256px;

        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-end;

        .password-inputs {
            width: 256px;
            display: flex;
            justify-content: space-between;

            margin-bottom: 24px;

            span {
                width: 16px;
                height: 16px;

                border-radius: 100%;
                border: 1px solid var(--secondary-light-1);
                transition: all 0.3s ease-in-out;

                &.active {
                    border-color: var(--primary);
                }

                &.complete {
                    background-color: var(--primary);
                    border-color: var(--primary);
                }
            }
        }

        .pad-area {
            width: 100%;

            display: grid;
            grid-template-columns: auto auto auto;
            gap: 8px;
            justify-items: center;

            .button-remember-me-pad {
                width: 103px;

                &.icon-only {
                    margin: auto;
                }

                &.last-number-button {
                    grid-column-start: 2;
                }

                @media only screen and (max-width: 360px) {
                    width: 88px;
                }
            }
        }
    }

    &.mobile {
        max-width: 392px;
        min-height: 406px;

        @media only screen and (max-height: 800px) {
            border-bottom: 1px solid var(--secondary-light-2);
        }

        .personal-area {
            top: -67px;
            margin-bottom: -35px; // diference between the top value and the actual margin-bottom
        }

        .pad-area {
            height: 100%;
            max-height: 352px;
            margin-bottom: 78px;

            @media only screen and (max-height: 800px) {
                margin-bottom: 54px;
            }

            @media only screen and (max-height: 568px) {
                margin-bottom: 24px;
            }
        }
    }
}
</style>
